import { Injectable } from '@angular/core';
import { IFile } from 'src/sync';
import mimeData from '../../../mimeData';
import { CODE_EXTENSIONS, MARKDOWN_EXTENSIONS } from './text-file-type.constants';

export enum TextEditorVariant {
  TEXT = 'text',
  MARKDOWN = 'markdown',
  CODE = 'code',
}

@Injectable({
  providedIn: 'root',
})
export class TextFileTypeService {
  private textExtensions: Set<string> | null = null;

  public isTextExtension(ext: string): boolean {
    const normalized = this.normalizeExtension(ext);
    if (!normalized) {
      return false;
    }
    return (
      this.getTextExtensions().has(normalized) || CODE_EXTENSIONS.has(normalized) || MARKDOWN_EXTENSIONS.has(normalized)
    );
  }

  public isTextFile(item: IFile): boolean {
    if (!item || !item.name) {
      return false;
    }
    const ext = this.extractExtension(item.name);
    if (this.isTextExtension(ext)) {
      return true;
    }
    return !!item.mime_type && item.mime_type.startsWith('text/');
  }

  public getTextEditorVariant(ext: string): TextEditorVariant {
    const normalized = this.normalizeExtension(ext);
    if (MARKDOWN_EXTENSIONS.has(normalized)) {
      return TextEditorVariant.MARKDOWN;
    }
    if (CODE_EXTENSIONS.has(normalized)) {
      return TextEditorVariant.CODE;
    }
    return TextEditorVariant.TEXT;
  }

  public validateTextExtension(ext: string): { valid: boolean; error?: string } {
    const normalized = this.normalizeExtension(ext);
    if (!normalized || !this.isTextExtension(normalized)) {
      return { valid: false };
    }
    return { valid: true };
  }

  public getAllTextExtensions(): string[] {
    return Array.from(this.getTextExtensions()).sort();
  }

  private normalizeExtension(ext: string): string {
    if (!ext) {
      return '';
    }
    const trimmed = ext.trim().toLowerCase();
    return trimmed.startsWith('.') ? trimmed.slice(1) : trimmed;
  }

  private extractExtension(fileName: string): string {
    const idx = fileName.lastIndexOf('.');
    if (idx < 0 || idx === fileName.length - 1) {
      return '';
    }
    return fileName.slice(idx + 1).toLowerCase();
  }

  private getTextExtensions(): Set<string> {
    if (this.textExtensions === null) {
      const set = new Set<string>();
      for (const entry of mimeData) {
        if (!entry.name || !entry.name.startsWith('text/')) {
          continue;
        }
        for (const type of entry.types || []) {
          const normalized = this.normalizeExtension(type);
          if (normalized) {
            set.add(normalized);
          }
        }
      }
      this.textExtensions = set;
    }
    return this.textExtensions;
  }
}
